import { useMCPAuth } from "mcp/hooks/useMCPAuth";

/**
 * Custom hook to check if Cindra is enabled at both deployment and service levels.
 * 
 * This implements a layered check:
 * 1. Deployment level: VITE_CINDRA_ENABLED environment variable
 *    - If false, Cindra is disabled at deployment level (returns false immediately)
 *    - If true, proceeds to service level check
 * 
 * 2. Service level: MCP authentication status
 *    - Requires authentication and network access
 *    - Only checked if deployment level is enabled
 * 
 * @returns {boolean} true if Cindra is enabled at both deployment and service levels
 */
export const useCindraEnabled = (): boolean => {
    // Layer 1: Check deployment-level enablement (fast, no dependencies)
    const deploymentEnabled = import.meta.env.VITE_CINDRA_ENABLED === "true";

    // If deployment level is disabled, return false immediately
    if (!deploymentEnabled) {
        return false;
    }

    // Layer 2: Check service-level enablement (requires auth and network)
    const { isCindraEnabled: serviceEnabled } = useMCPAuth();

    return serviceEnabled;
};
